import { Button, Checkbox, Form, Popup, Spacing, Text } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { FC, memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';
import { useCompanyBetaFeatures } from 'shared/hooks/useCompanyBetaFeatures';

import { useEditTrackingCategoriesMutation } from '../../hooks/useEditTrackingCategoriesMutation';
import { useGetTrackingFields } from '../../hooks/useGetTrackingFields';
import CheckPeriodFormPart from '../CheckPeriodFormPart/CheckPeriodFormPart';
import TrackingCategoriesFormPart from '../TrackingCategoriesFormPart/TrackingCategoriesFormPart';
import { useCutsetValuesFromBudget } from '../TrackingCategoriesFormPart/TrackingCategoriesFormPart.hooks';
import ZeroAmountFormPart from '../ZeroAmountFormPart/ZeroAmountFormPart';
import { messages } from './TrackingCategoriesPopupContent.messages';
import { FormValues, TrackingCategoriesPopupContentProps } from './TrackingCategoriesPopupContent.types';

const TrackingCategoriesPopupContent: FC<TrackingCategoriesPopupContentProps> = memo((props) => {
    const { budget, toggleIsOpenPopup } = props;

    const company = useCompanyBasedOnUrl();
    const {
        changeBudget,
        isLoading: isLoadingChangeBudget,
        isSuccess: isSuccessChangeBudget,
    } = useEditTrackingCategoriesMutation(company);
    const { data: fieldsData, isLoading: isLoadingFields } = useGetTrackingFields(company.id);
    const { isBudgetingZeroAmounts } = useCompanyBetaFeatures(company.id);

    const isLoading = isLoadingChangeBudget || isLoadingFields;

    const cutset = useCutsetValuesFromBudget(budget, fieldsData);

    const form = useForm<FormValues>({
        values: {
            companyId: company.id,
            budgetId: budget.id,
            checkPeriod: budget.checkPeriod || domain.BudgetCheckPeriod.Monthly,
            checkPeriodFirstMonth: budget.checkPeriodFirstMonth,
            zeroAmountHandlingType: budget.zeroAmountHandlingType || domain.BudgetZeroAmountHandlingType.NotConsider,
            checkPeriodFrom: budget.checkPeriodFrom,
            checkPeriodTill: budget.checkPeriodTill,
            cutset,
        },
    });

    const onSubmit = form.handleSubmit((data: FormValues) => {
        if (
            data.checkPeriod !== domain.BudgetCheckPeriod.AnnuallyWithCustomFirstMonth &&
            data.checkPeriod !== domain.BudgetCheckPeriod.YearToDateWithCustomFirstMonth
        ) {
            delete data.checkPeriodFirstMonth;
        }

        delete data.agreed;

        changeBudget(data);
    });

    useEffect(() => {
        if (isSuccessChangeBudget) {
            toggleIsOpenPopup(false);
        }
    }, [isSuccessChangeBudget, toggleIsOpenPopup]);

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={messages.title({
                    name: budget.name,
                })}
                actions={
                    <Button disabled={isLoading} type='submit' color='blue80' size='medium'>
                        {messages.saveButton}
                    </Button>
                }
                progress={isLoading}
            />

            <Popup.Body>
                <Text font='body' spacing='0 0 24'>
                    {messages.description({
                        name: budget.name,
                        br: <br />,
                    })}

                    <br />

                    <a
                        href='https://support.approvalmax.com/portal/en/kb/approvalmax-1/approvalmax-for-xero/budget-checking'
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        {messages.learnMore}
                    </a>
                </Text>

                <TrackingCategoriesFormPart companyId={company.id} budget={budget} />

                <CheckPeriodFormPart origin={budget.origin} />

                <Spacing height={24} />

                <Checkbox name='agreed' required>
                    {messages.agreement}
                </Checkbox>

                {isBudgetingZeroAmounts && <ZeroAmountFormPart />}
            </Popup.Body>
        </Form>
    );
});

export default TrackingCategoriesPopupContent;
